import classNames from "classnames";
import { Button } from "components/buttons";
import { ShowHide } from "components/layout";
import { MediaList } from "components/media";
import { TemplateBuilderDatePreview } from "pages/checks/templates/builder/preview/questions/date";
import { TemplateBuilderDecimalPreview } from "pages/checks/templates/builder/preview/questions/decimal";
import { TemplateBuilderDropdownPreview } from "pages/checks/templates/builder/preview/questions/dropdown";
import { TemplateBuilderIntegerPreview } from "pages/checks/templates/builder/preview/questions/integer";
import { TemplateBuilderMediaCapturePreview } from "pages/checks/templates/builder/preview/questions/mediaCapture";
import { TemplateBuilderMultiSelectPreview } from "pages/checks/templates/builder/preview/questions/multiSelect";
import { TemplateBuilderTextPreview } from "pages/checks/templates/builder/preview/questions/text";
import { TemplateBuilderTextAreaPreview } from "pages/checks/templates/builder/preview/questions/textArea";
import { TemplateBuilderYesNoPreview } from "pages/checks/templates/builder/preview/questions/yesNo";
import { TemplateBuilderDefectCapturePreview } from "./defectCapture";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import { TemplateBuilderAssetLookupPreview } from "pages/checks/templates/builder/preview/questions/assetLookup";

export function TemplateBuilderPreviewQuestion(
    {
        repeatableTemplate = false,
        onAddMore = () => null,
        warningFooterMessage = "",
        onChange = () => null,
        helperImages = [],
        answersInScope,
        metaData,
        question
    }
) {
    const [showDetails, setShowDetails] = useState(false);

    const answers = answersInScope.filter(a => a.questionId === question.id);
    const isRepeatable = !!question.repeatability;

    const errorText = useMemo(() => {
        if (question.required && answers.some(a => !a.isAnswered)) {
            return "Required";
        }

        if (isRepeatable && answers.length - 1 < question.repeatability.minimumNumberOfRepeats) {
            return "More answers are required";
        }

        const firstErroredAnswer = answers.find(a => !!a.error);

        if (firstErroredAnswer) {
            return firstErroredAnswer.error;
        }

        return "";
    }, [answersInScope]);

    const renderQuestion = () => <div className="check-preview-answers-container">
        {answers.map((a, i) => renderQuestionInput(a, i))}
    </div>

    const renderQuestionInput = (answer, index) => {
        const canDelete = answers.length > 1;
        const canAddMore = isRepeatable
            && answers.length <= question.repeatability.maximumNumberOfRepeats
            && answers.length === index + 1;

        const addMoreButton = canAddMore ? <Button
            className="check-preview-repeatable-button spacer-top-small"
            onClick={onAddMore}
            text={"Add new row"}
            icon="add"
        /> : <></>

        const deleteButton = canDelete ? <Button
            icon='trash'
            iconOnly={true}
            minimal={true}
            className='check-preview-section-delete'
            onClick={() => onDelete(index)}
        /> : <></>;

        switch (question.$componentType) {
            case "IntegerQuestion":
                return (
                    <TemplateBuilderIntegerPreview
                        key={index}
                        answer={answer}
                        question={question}
                        onChange={(a) => onChangeValue(index, a)}
                        addAnotherComponent={addMoreButton}
                        deleteComponent={deleteButton}
                    />
                );
            case "DecimalQuestion":
                return (
                    <TemplateBuilderDecimalPreview
                        key={index}
                        answer={answer}
                        question={question}
                        onChange={(a) => onChangeValue(index, a)}
                        addAnotherComponent={addMoreButton}
                        deleteComponent={deleteButton}
                    />
                );
            case "TextAreaQuestion":
                return (
                    <TemplateBuilderTextAreaPreview
                        key={index}
                        answer={answer}
                        question={question}
                        onChange={(a) => onChangeValue(index, a)}
                        addAnotherComponent={addMoreButton}
                        deleteComponent={deleteButton}
                    />
                );
            case "TextQuestion":
                return (
                    <TemplateBuilderTextPreview
                        key={index}
                        answer={answer}
                        question={question}
                        onChange={(a) => onChangeValue(index, a)}
                        addAnotherComponent={addMoreButton}
                        deleteComponent={deleteButton}
                    />
                );
            case "DateQuestion":
                return (
                    <TemplateBuilderDatePreview
                        answer={answer}
                        question={question}
                        onChange={(a) => onChangeValue(index, a)}
                        addAnotherComponent={addMoreButton}
                        deleteComponent={deleteButton}
                    />
                );
            case "YesNoQuestion":
                return (
                    <TemplateBuilderYesNoPreview
                        key={index}
                        answer={answer}
                        question={question}
                        onChange={(a) => onChangeValue(index, a)}
                    />
                );
            case "DropdownQuestion":
                return (
                    <TemplateBuilderDropdownPreview
                        key={index}
                        answer={answer}
                        question={question}
                        onChange={(a) => onChangeValue(index, a)}
                        addAnotherComponent={addMoreButton}
                        deleteComponent={deleteButton}
                    />
                );
            case "MultiSelectQuestion":
                return (
                    <TemplateBuilderMultiSelectPreview
                        key={index}
                        answer={answer}
                        question={question}
                        onChange={(a) => onChangeValue(index, a)}
                        addAnotherComponent={addMoreButton}
                        deleteComponent={deleteButton}
                    />
                );
            case "MediaCaptureQuestion":
                return (
                    <TemplateBuilderMediaCapturePreview
                        key={index}
                        answer={answer}
                        question={question}
                        onChange={(a) => onChangeValue(index, a)}
                    />
                );
            case "DefectCaptureQuestion":
                return (
                    <TemplateBuilderDefectCapturePreview
                        key={index}
                        answer={answer}
                        question={question}
                        onChange={(a) => onChangeValue(index, a)}
                    />
                );
            case "AssetLookupQuestion":
                return (
                    <TemplateBuilderAssetLookupPreview
                        key={index}
                        answer={answer}
                        question={question}
                        onChange={(a) => onChangeValue(index, a)}
                    />
                );
            default:
                return <span>Preview not implemented for question type <strong>{question.$componentType}</strong></span>;
        }
    };

    const onDelete = (index) => {
        const answersClone = [...answers];
        answersClone.splice(index, 1);
        onChange(answersClone);
    }

    const onChangeValue = (index, answer) => {
        if (!isRepeatable) {
            onChange(answer);
        } else {
            const answersClone = [...answers];
            answersClone[index] = { ...answer };
            onChange(answersClone);
        }
    }

    return (
        <div className={classNames("check-preview-section")}>
            <div className="check-preview-section-metadata">
                {metaData}
                <span> &#183; </span>
                <ShowHide
                    evaluator={errorText}
                    show={<span className="additional-data danger">{errorText}</span>}
                    hide={<span className="additional-data">{question.required ? "Required" : "Optional"}</span>}
                />

            </div>
            <div className="check-preview-section-title">
                <h4>{question.title}</h4>
            </div>
            <ShowHide
                evaluator={!!question.helperText || helperImages.length > 0}
                show={<div className="check-preview-session-show-hide">
                    <details onToggle={() => setShowDetails(s => !s)} >
                        <summary>{showDetails ? 'Hide' : 'Show'} question help</summary>
                        <p className="check-preview-session-show-hide-content">{question.helperText}</p>
                        {helperImages.length > 0 && (
                            <MediaList showMediaCounter={false} showPlaceholder={false} media={helperImages} size="small" renderMediaViewer={({ selectedMedia, showMedia, onHideMedia }) => (
                                showMedia && (
                                    <div className="check-preview-inner-modal-overlay" onKeyDown={onHideMedia} onClick={onHideMedia} role="none">
                                        <div className="check-preview-inner-modal-content">
                                            <img src={selectedMedia} alt="Preview" />
                                        </div>
                                    </div>
                                )
                            )} />
                        )}
                    </details>
                </div>}
            />
            <div className="check-preview-question-container">
                <hr></hr>
                {renderQuestion()}
            </div>
            <ShowHide
                evaluator={!!warningFooterMessage}
                show={<div className="check-preview-section-footer-warning">
                    {warningFooterMessage}
                </div>}
            />
        </div>
    );
}

TemplateBuilderPreviewQuestion.propTypes = {
    question: PropTypes.object.isRequired,
    answersInScope: PropTypes.array.isRequired,
    onChange: PropTypes.func,
    onAddMore: PropTypes.func,
    repeatableTemplate: PropTypes.bool,
    warningFooterMessage: PropTypes.string,
    metaData: PropTypes.string.isRequired,
    helperImages: PropTypes.array,
};